import { useCallback, useEffect, useState } from "react";

import { usePageNameContext } from "../../contexts/PageTitleContext";
import useCustomerDetails from "../../data/useCustomerDetails";
import { useFeatureEnabled } from "../../hooks/useFeatureFlag";
import LoadingWheel from "../../components/shared/LoadingWheel";
import { ButtonInput } from "../../components/shared/Inputs/ButtonInput";
import { ListedTableStats } from "../../components/ebayListings/listedTable/Stats/ListedTableStats";
import EbayOptimisationTable from "../../components/ebayOptimisation/EbayOptimisationTable";

const guideCards = [
  {
    number: "1",
    numberClasses: "bg-green-50 text-green-700",
    title: "Find listings",
    description:
      "Filter by category, warehouse location, date range, or missing fields to narrow the active listings you want to work on.",
  },
  {
    number: "2",
    numberClasses: "bg-blue-50 text-blue-700",
    title: "Get intelligence",
    description:
      "Select rows and run Get Intelligence for market prices. Use Stage avg price on a row, or open the stage editor to set a new price.",
  },
  {
    number: "3",
    numberClasses: "bg-indigo-50 text-indigo-700",
    title: "Stage changes",
    description:
      "Edit a single row or use bulk actions to stage title, policies, Wilma titles, image rules, remove BG on 1st image, or apply category defaults. Nothing is sent to eBay yet.",
  },
  {
    number: "4",
    numberClasses: "bg-amber-50 text-amber-700",
    title: "Review staged",
    description:
      "Check the Staged Changes column for before/after diffs and whether the listing is eligible to relist.",
  },
  {
    number: "5",
    numberClasses: "bg-rose-50 text-rose-700",
    title: "Send to Relist",
    description:
      "When ready, Send to Relist (optionally Append SKU). Use Discard Changes to clear staged work without touching the live listing.",
  },
];

export default function EbayOptimisation() {
  const { setInfo } = usePageNameContext();
  const { features } = useCustomerDetails();
  const optimisationEnabled = useFeatureEnabled("eBay Optimisation");
  const [helpOpen, setHelpOpen] = useState(false);
  const [selectedMissingFields, setSelectedMissingFields] = useState<string[]>([]);

  const toggleMissingField = useCallback((key: string) => {
    setSelectedMissingFields((prev) =>
      prev.includes(key) ? prev.filter((f) => f !== key) : [...prev, key]
    );
  }, []);

  useEffect(() => {
    setInfo({
      name: "eBay Optimisation",
      desc: "Stage changes to active listings and send them to be relisted",
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (features.isLoading) {
    return <LoadingWheel />;
  }

  if (!optimisationEnabled) {
    return (
      <div className="mt-8 rounded-md border border-gray-200 bg-white p-6 text-center">
        <h2 className="text-lg font-medium text-gray-900">eBay Optimisation is not enabled</h2>
        <p className="mt-2 text-sm text-gray-600">
          This feature is not enabled for your account. Contact support if you'd like it turned on.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 mt-4">
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5 space-y-4">
        <div className="flex items-center justify-between gap-4">
          <div className="space-y-2">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-2xl font-semibold text-gray-900">Listing optimisation</h3>
              <span className="inline-flex items-center rounded-full bg-blue-50 text-blue-700 text-xs font-semibold px-2.5 py-1">
                Stage → Relist
              </span>
            </div>
            <p className="text-sm text-gray-600">
              Stage title, price, policy and image changes against active listings, then send them to be relisted when you are ready.
            </p>
          </div>
          <ButtonInput
            label={helpOpen ? "Hide Guide" : "Show Guide"}
            isSubmit={false}
            onClick={() => setHelpOpen((prev) => !prev)}
            classes="!bg-white !text-gray-700 border !border-gray-300 hover:!bg-gray-50 shrink-0"
          />
        </div>

        {helpOpen && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3">
              {guideCards.map((card) => (
                <div key={card.number} className="border border-gray-200 rounded-xl p-4 bg-white">
                  <span
                    className={`inline-flex h-8 w-8 items-center justify-center rounded-md text-sm font-semibold ${card.numberClasses}`}
                  >
                    {card.number}
                  </span>
                  <p className="mt-3 text-xl font-semibold text-gray-900">{card.title}</p>
                  <p className="mt-1 text-sm text-gray-600 leading-5">{card.description}</p>
                </div>
              ))}
            </div>

            <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 flex items-start gap-3">
              <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700 text-sm font-bold">
                !
              </span>
              <div>
                <p className="text-base font-semibold text-amber-900">Staged changes stay local</p>
                <p className="text-sm text-amber-800">
                  Nothing on this page updates the live eBay listing until you use Send to Relist. Update Aspects
                  changes item specifics in Wilma immediately and is separate from the stage → relist flow.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      <ListedTableStats
        selectedMissingFields={selectedMissingFields}
        onToggleMissingField={toggleMissingField}
      />
      <EbayOptimisationTable
        selectedMissingFields={selectedMissingFields}
        onToggleMissingField={toggleMissingField}
      />
    </div>
  );
}
